import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { DatabaseInconsistencyLogResultItem, OrganizationDatabaseInconsistencyLogResultStore } from 'src/stores/organizations-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { Card, Row, Tag } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { CheckSquareOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { OrganizationLogsContext } from '..';
import { emptyTableModel } from 'src/common/utils';
import { formatMessage } from 'src/core/services/http.service';
import { ENV } from 'src/';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';

const OrganizationDbInconsistencyLogsResult: FC = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const config: AppConfiguration = useContext(AppConfig);
  const logId = params.id!;

  const { setActiveTab } = useContext(OrganizationLogsContext);

  const [query, setQuery] = useState({
    searchQuery: '',
    skip: 0,
    take: 10,
  } as Query);

  const logResultStore = container.get(OrganizationDatabaseInconsistencyLogResultStore);
  const logResultState = logResultStore.state;

  if (location.state) setHeader(t('DatabaseCheckResultOnDate').replace('{0}', location.state.date));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'organizations', breadcrumbName: t('Organization') },
    { path: 'organizations/logs', breadcrumbName: t('OrganizationLogs') },
    { path: `organizations/logs/inconsistency-logs/${logId}`, breadcrumbName: t('DatabaseInconsistencyLogs') },
  ]);

  useEffect(
    () => {
      if (!location.state) {
        navigate('/organizations');
        return;
      }

      logResultStore.loadById(logId, query);
      setActiveTab('database-inconsistency');
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query,
    columns: [
      {
        title: t('Name'),
        field: 'scopeName',
      },
      {
        title: t('ScopeType'),
        field: 'scopeType',
        renderer: (data) => <Tag>{t(data.scopeType)}</Tag>,
      },
      {
        title: t('Parent'),
        field: 'parentName',
      },
      {
        title: t('Parent Type'),
        field: 'parentType',
        renderer: (data) => <Tag>{data.parentType}</Tag>,
      },
      {
        title: t('Problem'),
        field: 'problem',
      },
      {
        title: t('Fixed'),
        field: 'fixed',
        renderer: (data) =>
          data.fixed ? <CheckSquareOutlined style={{ color: '#b7eb8f' }} /> : <CloseSquareOutlined style={{ color: '#ffccc7' }} />,
      },
    ],
    data: { ...logResultState.value, isBusy: logResultState.value.isBusy },
  } as TableModel<DatabaseInconsistencyLogResultItem>;

  return (
    <Row align="top" justify="space-between">
      <Card className="card-table" style={{ width: '100%' }}>
        <TableView
          rowKey="id"
          canCreateNew={false}
          canDelete={false}
          canEdit={false}
          canSort
          onQueryChanged={(query: Query) => {
            setQuery(query);
            logResultStore.loadById(logId, query);
          }}
          error={
            logResultState.errorMessage.value &&
            (config.environment === ENV.DEV ? formatMessage(logResultState.errorMessage.value) : t('OrganizationsTableError'))
          }
          onRefresh={() => logResultStore.loadById(logId, query)}
          model={logResultState.errorMessage.value ? emptyTableModel : tableModel}
        />
      </Card>
    </Row>
  );
};

export default OrganizationDbInconsistencyLogsResult;
